import { Injectable } from '@angular/core'
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams,
} from '@angular/common/http'
import { Observable, retry, RetryConfig, throwError } from 'rxjs'
import { catchError } from 'rxjs/operators'
import { environment } from '../../../environments/environment'
import { NotificationService } from '../notification.service'
import { AuthService } from '../auth.service'
import { RequestCancellationService } from '../request-cancellation.service'

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  public baseUrl = environment.apiUrl

  constructor(
    private http: HttpClient,
    private notificationService: NotificationService,
    private authService: AuthService,
    private requestCancellationService: RequestCancellationService,
  ) {}

  private getHeaders(customHeader: { [key: string]: any } = {}): HttpHeaders {
    const oktaToken = this.authService.getStoredToken()
    let tokenHeader = {}
    if (oktaToken) {
      tokenHeader = {
        Authorization: `Bearer ${oktaToken}`,
      }
    }
    return new HttpHeaders({
      ...customHeader,
      ...tokenHeader,
    })
  }

  public get<T>(
    path: string,
    params?: { [key: string]: any },
    customHeader: { [key: string]: any } = {},
    extra: { [key: string]: any } = {},
  ): Observable<T> {
    const headers = this.getHeaders(customHeader)
    let httpParams = new HttpParams()

    if (params) {
      Object.keys(params).forEach((key) => {
        if (params[key] !== null && params[key] !== undefined) {
          httpParams = httpParams.append(key, params[key])
        }
      })
    }

    return this.http
      .get<T>(`${this.baseUrl}${path}`, {
        headers,
        params: httpParams,
        ...extra,
      })
      .pipe(catchError(this.handleError))
  }

  public post<T>(
    path: string,
    body: FormData,
    customHeader: { [key: string]: any } = {},
  ): Observable<T> {
    const headers = this.getHeaders(customHeader)

    return this.http
      .post<T>(`${this.baseUrl}${path}`, body, { headers })
      .pipe(catchError(this.handleError))
  }

  public put<T>(path: string, body: any): Observable<T> {
    const headers = this.getHeaders()

    return this.http
      .put<T>(`${this.baseUrl}${path}`, body, { headers })
      .pipe(catchError(this.handleError))
  }

  public delete<T>(path: string): Observable<T> {
    const headers = this.getHeaders()

    return this.http
      .delete<T>(`${this.baseUrl}${path}`, { headers })
      .pipe(catchError(this.handleError))
  }

  public patch<T>(
    path: string,
    body?: any,
    retryConfig?: RetryConfig,
  ): Observable<T> {
    const headers = this.getHeaders()
    return this.http
      .patch<T>(`${this.baseUrl}${path}`, body, { headers })
      .pipe(retryConfig ? retry(retryConfig) : retry(0))
  }

  public handleError = (error: HttpErrorResponse) => {
    if (error.status === 401) {
      this.notificationService.showToast(
        'error',
        'ERRORS.ERROR_TITLE',
        'ERRORS.ERROR_AUTH',
      )
    } else {
      this.notificationService.showToast(
        'error',
        'ERRORS.ERROR_TITLE',
        error.message,
      )
    }

    return throwError(() => ({
      status: error.status,
      message: error.message || 'Une erreur est survenue',
      error: error.error,
    }))
  }
}
